import { AxiosInstance, AxiosResponse } from "axios";
import { fetcher } from "../../configurations";
import {
    ShipmentWeekRow,
    ShipmentYearRow,
    ShipmentsByDateRangeResponse,
    ShipmentsOverviewResponse,
} from "../../interfaces";

export class ShipmentsRepository {
    private lmBootApi: AxiosInstance;

    constructor() {
        this.lmBootApi = fetcher(process.env.REACT_APP_URL_API_2 as string);
    }

    /**
     * POST /shipments/date-range
     * Body: { startDate: "YYYY-MM-DD", endDate: "YYYY-MM-DD" }
     *
     * Expected API response shape (so the UI can display the weekly view table):
     * {
     *   data: [
     *     {
     *       fecha: "Saturday, September 11, 2021",   // Human-readable date (optional; process can build from fechaKey)
     *       fechaKey: "2021-09-11",                   // ISO date string for keying (YYYY-MM-DD)
     *       ajustes: 0,
     *       nuevas: 0,
     *       reparaciones: 0,
     *       reposiciones: 0,
     *       adicionales: 0,
     *       totalBotas: 0
     *     },
     *     // ... one object per day in the range (startDate through endDate inclusive)
     *   ]
     * }
     */
    getShipmentsByDateRange(
        startDate: string,
        endDate: string
    ): Promise<AxiosResponse<ShipmentsByDateRangeResponse>> {
        return this.lmBootApi.post<ShipmentsByDateRangeResponse>("/shipments/date-range", {
            startDate,
            endDate,
        });
    }

    /**
     * GET /shipments/overview
     * Query params: year (number, e.g. 2024)
     *
     * Expected API response shape (so the UI can display the year overview table):
     * {
     *   data: [
     *     { mes: "ENERO",    ajustes: 28, nuevas: 98,  reparaciones: 12, reposiciones: 2, stock: 0, totalBotas: 140 },
     *     { mes: "FEBRERO",  ajustes: 22, nuevas: 87,  reparaciones: 15, reposiciones: 1, stock: 0, totalBotas: 125 },
     *     { mes: "MARZO",    ajustes: 35, nuevas: 124, reparaciones: 18, reposiciones: 3, stock: 0, totalBotas: 180 },
     *     { mes: "ABRIL",    ajustes: 30, nuevas: 112, reparaciones: 14, reposiciones: 2, stock: 0, totalBotas: 158 },
     *     { mes: "MAYO",    ajustes: 26, nuevas: 95,  reparaciones: 11, reposiciones: 0, stock: 0, totalBotas: 132 },
     *     { mes: "JUNIO",   ajustes: 32, nuevas: 138, reparaciones: 16, reposiciones: 4, stock: 0, totalBotas: 190 },
     *     { mes: "JULIO",   ajustes: 29, nuevas: 119, reparaciones: 13, reposiciones: 2, stock: 0, totalBotas: 163 },
     *     { mes: "AGOSTO",  ajustes: 24, nuevas: 102, reparaciones: 10, reposiciones: 1, stock: 0, totalBotas: 137 },
     *     { mes: "SEPTIEMBRE", ajustes: 31, nuevas: 131, reparaciones: 17, reposiciones: 5, stock: 0, totalBotas: 184 },
     *     { mes: "OCTUBRE", ajustes: 27, nuevas: 108, reparaciones: 12, reposiciones: 3, stock: 0, totalBotas: 150 },
     *     { mes: "NOVIEMBRE", ajustes: 33, nuevas: 145, reparaciones: 19, reposiciones: 4, stock: 0, totalBotas: 201 },
     *     { mes: "DICIEMBRE", ajustes: 26, nuevas: 152, reparaciones: 15, reposiciones: 5, stock: 0, totalBotas: 198 }
     *   ]
     * }
     * - mes: Spanish month name (ENERO through DICIEMBRE).
     * - totalBotas per row = ajustes + nuevas + reparaciones + reposiciones (stock is typically 0).
     * - Exactly 12 items, one per month, in calendar order.
     */
    getShipmentsOverview(year: number): Promise<AxiosResponse<ShipmentsOverviewResponse>> {
        return this.lmBootApi.get<ShipmentsOverviewResponse>(`/shipments/overview/${year}`);
    }
}
